import React, { useEffect, useState } from 'react';
import { Pie } from '@ant-design/plots';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StatusPoint } from 'store/ducks/Global/types';
import { SEVERITY_COLOR_MAP } from 'store/ducks/InternalControls/types';
import { RootState } from 'store/configureStore';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import * as Styled from './Dashboard.styled';

interface RiskInstance {
  instance_id: number;
  severity: {
    id: number;
    name: string;
  } | null;
}

interface Risk {
  id: number;
  title: string;
  instances: RiskInstance[];
}

interface RiskMaintenanceResponse {
  status: string;
  data: Risk[];
}

const SEVERITY_LEVELS = [
  'Extreme',
  'Very High',
  'High',
  'Medium',
  'Low',
  'Negligible'
];

/* ---------------- CREATE SEVERITY DATA ---------------- */

const createSeverityData = (risks: Risk[]): StatusPoint[] => {
  const counts: Record<string, number> = {
    Extreme: 0,
    'Very High': 0,
    High: 0,
    Medium: 0,
    Low: 0,
    Negligible: 0
  };

  risks.forEach((risk) => {
    (risk.instances || []).forEach((instance) => {
      const severityName = instance.severity?.name?.trim();

      if (severityName && severityName in counts) {
        counts[severityName] += 1;
      }
    });
  });

  return SEVERITY_LEVELS.map((severity) => ({
    type: severity,
    value: counts[severity]
  })).filter((item) => item.value > 0);
};

/* ---------------- PIE CONFIG BUILDER ---------------- */

const createPieConfig = (data: StatusPoint[]) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  return {
    data,
    angleField: 'value',
    colorField: 'type',
    scale: {
      color: {
        range: data.map((d) => SEVERITY_COLOR_MAP[d.type])
      }
    },
    height: 360,

    label: {
      text: (datum: StatusPoint) => {
        if (!datum.value || total === 0) {
          return '';
        }

        const percent = ((datum.value / total) * 100).toFixed(0);

        return `${percent}%`;
      },

      style: {
        fontWeight: 'bold',
        fontSize: 13
      }
    },

    tooltip: {
      items: [
        (datum: StatusPoint) => {
          const percent =
            total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';

          return {
            name: datum.type,
            value: `${percent}% (${datum.value})`
          };
        }
      ]
    },

    legend: {
      position: 'bottom'
    },

    interaction: {
      elementHighlight: true
    }
  };
};

/* ---------------- DASHBOARD ---------------- */

const DashboardBPR: React.FC = () => {
  const [riskSeverityData, setRiskSeverityData] = useState<StatusPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRiskMaintenance = async () => {
      try {
        const response = await ApiService.getRiskMaintenance();
        const result = response.data as RiskMaintenanceResponse;

        if (result.status === 'success') {
          setRiskSeverityData(createSeverityData(result.data));
        }
      } catch (error) {
        console.error('Failed to fetch risk maintenance report:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRiskMaintenance();
  }, []);

  const pieConfig = createPieConfig(riskSeverityData);

  const { t } = useTranslation();
  const scheme = useSelector((state: RootState) => state.global.scheme);

  return (
    <Styled.DashboardContainer scheme={scheme}>
      <TitleBar
        title={t('BPR_DASHBOARD_TITLE')}
        goBackButtons={
          <GoBackButton to='/internal_controls' text={t('GO_BACK')} />
        }
      />

      <Styled.GridRow>
        <Styled.Card>
          <Styled.CardTitle>Risk Severity</Styled.CardTitle>

          <Styled.ChartWrapper>
            {loading ? <div style={{ height: 360 }} /> : <Pie {...pieConfig} />}
          </Styled.ChartWrapper>
        </Styled.Card>
      </Styled.GridRow>
    </Styled.DashboardContainer>
  );
};

export default DashboardBPR;
